import { Divider, Flex, Group, IconFont, Input, SegmentedControl, Select, Switch, Text, Tooltip } from '@byecode/ui'
import { type Field, type FieldType, VariableType } from '@lighthouse/core'
import {
    defaultFileMaxUploadSize,
    fileSizeFilter,
    getFileTypeByUrl,
    getImageFullUrlInApplication,
    getIsFindUseInVariable,
    getVideoSource,
    InnerTypeMapByFieldType,
    isTextValue,
    ListItem4ByecodeUi,
    useAtomData,
    useFindUseObjectContext,
    useSpaceQuotaContext,
    VariableSelect,
    VIDEO_RATIO_OPTIONS,
    videoFileSizeFilter
} from '@lighthouse/shared'
import React, { useCallback, useMemo, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import styled from 'styled-components'

import { lastPageOfStackAtom, pageAtomFamily, pageVariableSchemasFamily } from '@/atoms/page/state'
import { AnchorSetting } from '@/components/AnchorSetting'
import { getBlockAndPageDesignLimit } from '@/components/StyleSetting'
import AnimationSetting from '@/components/StyleSetting/AnimationSetting'
import { PositionSetting } from '@/components/StyleSetting/PositionSetting'
import { SizeSetting } from '@/components/StyleSetting/SizeSetting'
import { VisibilitySetting } from '@/components/VisibilitySetting'
import { useCurrentAppId } from '@/hooks/useApplication'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'
import { useGetPageParameterAndVariableOptions, useGetUserRecordOptions } from '@/hooks/usePage'
import { useParseVariableWithSubscription } from '@/hooks/useParseVariable'
import { useVariableCustomViewOption } from '@/hooks/useVariableCustomViewOption'
import { uploadManagerInAppParams } from '@/utils/auth'

import { BlockConfigureSegmentedControl } from '../Common/BlockConfigureSegmentedControl'
import { CursorSetting } from '../Common/CursorSetting'
import { SwitchControl } from '../Common/SwitchControl'
import type { BlockSettingsSegmentedEnum } from '../constants'
import { BLOCK_SETTINGS_SEGMENTED_ENUM, VIDEO_FIT_OPTIONS } from '../constants'

const SCxStyleContainer = styled.div`
    display: flex;
    flex-direction: column;
    /* gap: 8px; */
`
const saveFields = new Set<FieldType>(['video', 'file'])

const FILE_FIELD: Field = { type: 'file', id: '', dsId: '', name: '', innerType: InnerTypeMapByFieldType['file'] }
const VIDEO_FIELD: Field = { type: 'video', id: 'video', dsId: '', name: '视频', innerType: 'ARRAY' }

export const VideoBlockSetting: React.FC = () => {
    const appId = useCurrentAppId()
    const styleProps = getBlockAndPageDesignLimit('video')
    const disabledWithVersion = useIsDisabledWithVersion()
    const { control, register, watch } = useFormContext /* <VideoBlockConfig> */()
    const [stackId, pageId] = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => [s?.stackId || '', s?.pageId || ''], [])
    )
    const findUseObject = useFindUseObjectContext()
    const { quota } = useSpaceQuotaContext()
    const pageName = useAtomData(
        pageAtomFamily(pageId),
        useCallback(s => s?.name ?? '', [])
    )
    const pageVariableSchemas = useAtomData(pageVariableSchemasFamily(pageId))

    const [videoSource, isAuto] = watch(['videoSource', 'isAuto'])
    const fileMaxUploadSize = quota?.uploadFileLimitSize ?? defaultFileMaxUploadSize
    const fileUploadProps = useMemo(
        () => ({
            multiple: false,
            uploadOptions: {
                info: { id: '', label: pageName, groupId: pageId },
                options: {
                    ...uploadManagerInAppParams(),
                    fileFilter: fileSizeFilter(fileMaxUploadSize)
                }
            }
        }),
        [pageId, pageName, fileMaxUploadSize]
    )

    const videoUploadProps = useMemo(
        () => ({
            multiple: false,
            accept: 'video/*',
            uploadOptions: {
                info: { id: '', label: pageName, groupId: pageId },
                options: {
                    ...uploadManagerInAppParams(),
                    fileFilter: videoFileSizeFilter(fileMaxUploadSize)
                }
            }
        }),
        [fileMaxUploadSize, pageId, pageName]
    )

    const parseVariable2Field = useParseVariableWithSubscription(videoSource)

    const videoSourceType = useMemo(() => {
        const DEFAULT_SOURCE = ''
        if (!videoSource) {
            return 'byecode'
        }
        const cellValue = parseVariable2Field(videoSource)
        if (!cellValue) {
            return 'byecode'
        }
        if (cellValue.type === 'video' || cellValue.type === 'file') {
            const videoValue =
                cellValue.type === 'file' ? cellValue?.value?.find(v => getFileTypeByUrl(v) === 'video') : cellValue?.value?.[0]
            const videoUrl = cellValue.value ? getImageFullUrlInApplication(appId, videoValue) || DEFAULT_SOURCE : DEFAULT_SOURCE
            return getVideoSource(videoUrl)
        }
        return 'other'
    }, [appId, parseVariable2Field, videoSource])

    const { customViewOption } = useVariableCustomViewOption()
    const pageOptions = useGetPageParameterAndVariableOptions({ pageId })
    const userOption = useGetUserRecordOptions()

    const isUploadVideoSource = useMemo(() => {
        const { type } = videoSource ?? {}
        switch (type) {
            case VariableType.VALUE: {
                const value = videoSource?.type === VariableType.VALUE ? videoSource?.valueVariable?.value : ''
                return isTextValue(value) ? !value.includes('http') : false
            }
            case VariableType.USER: {
                const value = videoSource?.type === VariableType.USER ? videoSource?.userVariable?.fieldId : ''
                return userOption?.children?.find(v => v.value === value)?.fieldType === 'video'
            }
            case VariableType.VIEW: {
                const value = videoSource?.type === VariableType.VIEW ? videoSource?.viewVariable?.fieldId : ''
                return customViewOption?.children?.find(v => v.value === value)?.fieldType === 'video'
            }
            default: {
                return false
            }
        }
    }, [videoSource, userOption?.children, customViewOption?.children])

    const [currentTab, setCurrentTab] = useState<BlockSettingsSegmentedEnum>(BLOCK_SETTINGS_SEGMENTED_ENUM.DESIGN)

    return (
        <>
            <BlockConfigureSegmentedControl value={currentTab} onChange={setCurrentTab} />

            {currentTab === BLOCK_SETTINGS_SEGMENTED_ENUM.DESIGN && (
                <>
                    <PositionSetting {...styleProps.position} />
                    <Divider />
                    <SizeSetting {...styleProps.size} />

                    <Divider color="var(--color-gray-200)" />
                    <Group label="设计">
                        <SCxStyleContainer>
                            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                <Flex gap={4}>
                                    <Text>封面</Text>
                                </Flex>
                                <Controller
                                    name="cover"
                                    control={control}
                                    render={({ field: { value, onChange } }) => {
                                        const isHighLight = getIsFindUseInVariable({
                                            variable: value,
                                            findUseObject,
                                            pageVariableSchemas
                                        })
                                        return (
                                            <VariableSelect
                                                innerTypes={[FILE_FIELD.innerType]}
                                                field={FILE_FIELD}
                                                disabled={disabledWithVersion}
                                                disablePexels
                                                highlighting={isHighLight}
                                                uploadProps={fileUploadProps}
                                                viewOptions={customViewOption ? [customViewOption] : undefined}
                                                pageOptions={pageOptions}
                                                userOption={userOption}
                                                value={value}
                                                onChange={onChange}
                                            />
                                        )
                                    }}
                                />
                            </ListItem4ByecodeUi>
                            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                <Text>纵横比</Text>
                                <Controller
                                    name="ratio"
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            style={{
                                                width: 180
                                            }}
                                            searchable={false}
                                            options={VIDEO_RATIO_OPTIONS}
                                            value={field.value}
                                            onChange={field.onChange}
                                        />
                                    )}
                                />
                            </ListItem4ByecodeUi>
                            {videoSourceType === 'byecode' && (
                                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                    <Text>布局</Text>
                                    <div style={{ width: 180 }}>
                                        <Controller
                                            name="fitType"
                                            control={control}
                                            render={({ field }) => (
                                                <SegmentedControl
                                                    data={VIDEO_FIT_OPTIONS}
                                                    fullWidth
                                                    value={field.value}
                                                    onChange={v => field.onChange(v)}
                                                />
                                            )}
                                        />
                                    </div>
                                </ListItem4ByecodeUi>
                            )}
                            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                <Text>圆角</Text>
                                <Input
                                    {...register('radius')}
                                    style={{
                                        width: 180
                                    }}
                                    type="number"
                                    min={0}
                                    placeholder="请输入圆角"
                                />
                            </ListItem4ByecodeUi>
                            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                <Text>加载时显示加载图标</Text>
                                <Controller
                                    control={control}
                                    name="showLoading"
                                    defaultValue
                                    render={({ field }) => (
                                        <Switch
                                            checked={field.value}
                                            onChange={e => {
                                                field.onChange?.(e.currentTarget.checked)
                                            }}
                                        />
                                    )}
                                />
                            </ListItem4ByecodeUi>
                        </SCxStyleContainer>
                    </Group>

                    <Divider />
                    <Controller name="cursor" render={({ field }) => <CursorSetting {...field} />} />
                </>
            )}

            {currentTab === BLOCK_SETTINGS_SEGMENTED_ENUM.CONFIGURE && (
                <>
                    <Group label="基础">
                        <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                            <Text>视频来源</Text>
                            <Controller
                                name="videoSource"
                                control={control}
                                render={({ field }) => {
                                    const isHighLight = getIsFindUseInVariable({
                                        variable: field.value,
                                        findUseObject,
                                        pageVariableSchemas
                                    })
                                    return (
                                        <VariableSelect
                                            innerTypes={['ARRAY', 'TEXT']}
                                            field={VIDEO_FIELD}
                                            viewOptions={customViewOption ? [customViewOption] : undefined}
                                            pageOptions={pageOptions}
                                            userOption={userOption}
                                            highlighting={isHighLight}
                                            withinPortal
                                            disablePexels
                                            disabled={disabledWithVersion}
                                            placeholder="粘贴视频链接"
                                            uploadProps={videoUploadProps}
                                            value={field.value}
                                            onChange={field.onChange}
                                        />
                                    )
                                }}
                            />
                        </ListItem4ByecodeUi>
                        {!isUploadVideoSource && (
                            <div style={{ margin: '4px 0 8px 0' }}>
                                <Text
                                    lineHeight="22px"
                                    whiteSpace="pre-wrap"
                                    style={{
                                        padding: '8px 12px',
                                        backgroundColor: '#f3f3f3',
                                        color: 'var(--color-gray-500)',
                                        borderRadius: 6
                                    }}
                                >
                                    支持优酷、哔哩哔哩、YouTube 以及通用视频资源地址。
                                </Text>
                            </div>
                        )}
                        {/* {sourceType === 'default' ? <VideoUpload /> : <FieldSelect />} */}
                        {videoSourceType === 'other' && (
                            <Controller
                                name="isMute"
                                control={control}
                                render={({ field }) => (
                                    <SwitchControl
                                        label="静音"
                                        checked={field.value}
                                        onChange={e => field.onChange?.(e.currentTarget.checked)}
                                    />
                                )}
                            />
                        )}
                        {!['youku', 'bilibili'].includes(videoSourceType) && (
                            <Controller
                                name="isUsedVideoControl"
                                control={control}
                                render={({ field }) => (
                                    <SwitchControl
                                        label="视频控件"
                                        checked={field.value}
                                        onChange={e => field.onChange?.(e.currentTarget.checked)}
                                    />
                                )}
                            />
                        )}
                        {videoSourceType !== 'youku' && (
                            <Controller
                                name="isAuto"
                                control={control}
                                render={({ field }) => (
                                    <SwitchControl
                                        label="自动播放"
                                        checked={field.value}
                                        onChange={e => field.onChange?.(e.currentTarget.checked)}
                                    />
                                )}
                            />
                        )}
                        {isAuto && (
                            <Text
                                lineHeight="22px"
                                style={{ padding: '8px 12px', backgroundColor: '#f3f3f3', color: 'var(--color-gray-500)', borderRadius: 6 }}
                            >
                                由于微信的限制，微信浏览器中无法自动播放。
                            </Text>
                        )}
                        {videoSourceType === 'byecode' && (
                            <Controller
                                name="isVideoQualityLimit"
                                control={control}
                                render={({ field }) => (
                                    <SwitchControl
                                        label="开启流媒体"
                                        checked={field.value}
                                        onChange={e => field.onChange?.(e.currentTarget.checked)}
                                    />
                                )}
                            />
                        )}

                        {!['bilibili', 'youku'].includes(videoSourceType) && (
                            <>
                                <Controller
                                    name="isLoop"
                                    control={control}
                                    render={({ field }) => (
                                        <SwitchControl
                                            label="循环播放"
                                            checked={field.value}
                                            onChange={e => field.onChange?.(e.currentTarget.checked)}
                                        />
                                    )}
                                />
                                <Controller
                                    name="resumePlay"
                                    control={control}
                                    render={({ field }) => (
                                        <SwitchControl
                                            label={
                                                <Flex gap={4}>
                                                    <Text>自动回到上次播放位置</Text>
                                                    <Tooltip title="只会本地保存上次播放的位置">
                                                        <IconFont type="Question" fill="var(--color-gray-400)" />
                                                    </Tooltip>
                                                </Flex>
                                            }
                                            checked={field.value}
                                            onChange={e => field.onChange?.(e.currentTarget.checked)}
                                        />
                                    )}
                                />
                            </>
                        )}
                    </Group>
                    <Divider />
                    <AnchorSetting />
                    <Divider color="var(--color-gray-200)" />
                    <VisibilitySetting />
                </>
            )}

            {currentTab === BLOCK_SETTINGS_SEGMENTED_ENUM.INTERACTION && <AnimationSetting />}
        </>
    )
}
